import { useState, useRef, useEffect } from "react";
import { Input } from "./ui/input";
import { Check, ChevronDown, Search } from "lucide-react";

/**
 * Typeahead-Suche für Artikel (SKU, Name, Barcode).
 * Client-side filter — die volle Liste kommt via `items`-Prop.
 *
 * Props:
 *   value: string         Aktuelle item_id
 *   onChange: (id, item?) => void
 *   items: []             Volle Artikel-Liste
 *   placeholder?, testId?, disabled?
 */
export default function ItemAutocomplete({
  value, onChange, items = [],
  placeholder = "Artikel suchen…", testId = "item-autocomplete", disabled = false,
}) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(0);
  const ref = useRef(null);

  const selected = items.find((c) => c.id === value);

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const results = q.trim().length === 0
    ? items.slice(0, 15)
    : items.filter((it) => {
        const s = q.toLowerCase();
        return (it.name || "").toLowerCase().includes(s)
            || (it.sku  || "").toLowerCase().includes(s)
            || (it.barcode || "").toString().includes(s);
      }).slice(0, 15);

  const pick = (it) => { onChange(it.id, it); setQ(""); setOpen(false); };

  return (
    <div className="relative" ref={ref} data-testid={testId}>
      <button type="button" onClick={() => !disabled && setOpen((o) => !o)}
              disabled={disabled}
              data-testid={`${testId}-trigger`}
              className="w-full h-10 px-3 pr-10 rounded-lg border border-[var(--border)] bg-white text-left flex items-center justify-between text-sm hover:border-[var(--pop-pink)] transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
        <span className={selected ? "truncate" : "text-[var(--text-muted)]"}>
          {selected
            ? <><span className="font-mono text-xs mr-2 text-[var(--text-muted)]">{selected.sku}</span>{selected.name}</>
            : placeholder}
        </span>
        <ChevronDown size={16} className="text-[var(--text-muted)] flex-shrink-0" />
      </button>
      {open && !disabled && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-[var(--border)] rounded-lg shadow-lg max-h-72 overflow-hidden flex flex-col">
          <div className="relative border-b border-[var(--border)]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
            <Input autoFocus placeholder={`${items.length} Artikel — SKU, Name oder Barcode…`}
                   value={q}
                   data-testid={`${testId}-search`}
                   onChange={(e) => { setQ(e.target.value); setHi(0); }}
                   onKeyDown={(e) => {
                     if (e.key === "ArrowDown") { e.preventDefault(); setHi((h) => Math.min(h + 1, results.length - 1)); }
                     else if (e.key === "ArrowUp") { e.preventDefault(); setHi((h) => Math.max(h - 1, 0)); }
                     else if (e.key === "Enter") { e.preventDefault(); if (results[hi]) pick(results[hi]); }
                     else if (e.key === "Escape") setOpen(false);
                   }}
                   className="pl-8 border-0 rounded-none focus-visible:ring-0" />
          </div>
          <div className="overflow-y-auto flex-1">
            {results.length === 0 && <div className="p-4 text-center text-sm text-[var(--text-muted)]">Keine Treffer</div>}
            {results.map((it, i) => (
              <button key={it.id} type="button"
                      onClick={() => pick(it)}
                      onMouseEnter={() => setHi(i)}
                      data-testid={`${testId}-opt-${it.id}`}
                      className={`w-full px-3 py-2 text-left text-sm flex items-center justify-between hover:bg-[var(--pop-cream)]/60 ${i === hi ? "bg-[var(--pop-cream)]/40" : ""}`}>
                <div className="min-w-0 flex-1">
                  <div className="font-medium truncate">{it.name}</div>
                  <div className="text-xs text-[var(--text-muted)] truncate font-mono">
                    {[it.sku, it.barcode, `${it.price ?? "—"} €`].filter(Boolean).join(" · ")}
                  </div>
                </div>
                {value === it.id && <Check size={14} className="text-[var(--pop-pink)] flex-shrink-0 ml-2" />}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
